import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Search, Plus, Replace, Sparkles, Check, Loader2, AlertTriangle, Wrench, ShieldAlert } from 'lucide-react';
import { exercises as libraryExercises, isBaselineExercise } from '../data/exercises';
import { equipmentCategories } from '../data/equipment';
import { useProfile } from '../context/ProfileContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Quick-filter pills mirror the Library page so muscle taxonomy stays consistent.
// Expanded: added Palms, Wrists, Ankles, Neck, Hip Flexors, Adductors,
// Breath/Diaphragm to support PT/rehab + previously-missing customs.
const MUSCLE_PILLS = ['Chest', 'Back', 'Shoulders', 'Arms', 'Quads', 'Hamstrings', 'Glutes', 'Calves', 'Core', 'Forearms', 'Full Body', 'Palms', 'Wrists', 'Ankles', 'Neck', 'Hip Flexors', 'Adductors', 'Breath/Diaphragm'];

// Flat lookup: equipment id → human-readable name (for warning banner copy)
const EQUIPMENT_NAME = Object.fromEntries(
  equipmentCategories.flatMap((c) => c.items).map((i) => [i.id, i.name]),
);

/**
 * AddExerciseSheet — slide-up sheet for adding / replacing an exercise mid-workout.
 *
 * Filter-first UX: empty state until a muscle pill is tapped or query typed.
 * Avoids overwhelming scrolls as the library grows toward 700.
 *
 * Equipment guardrail: if a picked exercise needs gear not in profile.equipment
 * AND the user isn't fullGym, surface a banner with two actions:
 *   - Activate the missing equipment (persists to profile)
 *   - Add just for today anyway (transient)
 */
const AddExerciseSheet = ({ open, onClose, onSelect, mode = 'add', targetExerciseName = '', baselineOnly = false }) => {
  const { profile, update } = useProfile();
  const [tab, setTab] = useState('library');
  const [query, setQuery] = useState('');
  const [muscleFilter, setMuscleFilter] = useState(null);
  const [yourSavedOnly, setYourSavedOnly] = useState(false);
  const [activeTag, setActiveTag] = useState(null);
  const [customExercises, setCustomExercises] = useState([]);
  const [savedRefs, setSavedRefs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [pendingPick, setPendingPick] = useState(null); // { entry, missing: [equipmentId, …] }
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);

  // Custom form state
  const [customName, setCustomName] = useState('');
  const [customMuscle, setCustomMuscle] = useState('');
  const [customNotes, setCustomNotes] = useState('');
  const [customTags, setCustomTags] = useState('');
  const [submitToLibrary, setSubmitToLibrary] = useState(true);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setMuscleFilter(null);
    setYourSavedOnly(false);
    setActiveTag(null);
    setError('');
    setTab('library');
    setPendingPick(null);
    setSubmitConfirmOpen(false);
    axios.get(`${API}/custom-exercises`)
      .then((r) => setCustomExercises(r.data || []))
      .catch((e) => {
        // Surface auth failures so testers know their session lapsed instead of
        // seeing an empty "Your saved" list with no explanation. Network failures
        // stay silent — the offline queue will surface its own banner.
        if (e?.response?.status === 401 || e?.response?.status === 403) {
          setError('Your session expired. Sign out and sign back in to load saved exercises.');
        }
      });
    axios.get(`${API}/saved-library-refs`)
      .then((r) => setSavedRefs(r.data || []))
      .catch(() => { /* silent — refs follow custom-exercises auth state */ });
  }, [open]);

  const hasActiveScope = Boolean(query.trim() || muscleFilter || yourSavedOnly);

  // Tags the user has actually used across BOTH customs AND saved library refs.
  const userTags = useMemo(() => {
    const all = new Set();
    customExercises.forEach((ex) => (ex.tags || []).forEach((t) => all.add(t)));
    savedRefs.forEach((r) => (r.tags || []).forEach((t) => all.add(t)));
    return Array.from(all).sort();
  }, [customExercises, savedRefs]);

  // When Your Saved is active: union customs + resolved library refs into a
  // single browsable list. Each entry is shaped to feed completeSelect() —
  // library refs resolve to their underlying static exercise data.
  const filteredSaved = useMemo(() => {
    if (!yourSavedOnly) return [];
    const q = query.trim().toLowerCase();
    const refRows = savedRefs.map((r) => {
      const lib = libraryExercises.find((e) => e.id === r.library_exercise_id);
      if (!lib) return null;
      return { kind: 'ref', tags: r.tags || [], entry: lib };
    }).filter(Boolean);
    const customRows = customExercises.map((c) => ({
      kind: 'custom', tags: c.tags || [], entry: c,
    }));
    return [...refRows, ...customRows].filter((row) => {
      const ex = row.entry;
      if (muscleFilter && (ex.muscle || '').toLowerCase() !== muscleFilter.toLowerCase()) return false;
      if (activeTag && !row.tags.includes(activeTag)) return false;
      if (q && !(ex.name || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [yourSavedOnly, savedRefs, customExercises, query, muscleFilter, activeTag]);

  // Free-tier gate: when baselineOnly is true (free user), restrict the picker
  // to the same BASELINE_EXERCISE_IDS subset that the scheduled template uses.
  // This prevents free users from manually adding paid-locked exercises mid-session,
  // which would otherwise bypass the template-level gating entirely.
  const eligibleLibrary = useMemo(
    () => baselineOnly ? libraryExercises.filter(isBaselineExercise) : libraryExercises,
    [baselineOnly],
  );

  const filteredLibrary = useMemo(() => {
    if (!hasActiveScope || yourSavedOnly) return [];
    const q = query.trim().toLowerCase();
    return eligibleLibrary.filter((e) => {
      if (muscleFilter && e.muscle !== muscleFilter) return false;
      if (q && !(e.name.toLowerCase().includes(q) || (e.muscle || '').toLowerCase().includes(q) || (e.aliases || []).some((a) => a.toLowerCase().includes(q)))) return false;
      return true;
    });
  }, [query, muscleFilter, hasActiveScope, yourSavedOnly, eligibleLibrary]);

  const filteredCustom = useMemo(() => {
    // Legacy: when Your Saved is OFF, still show custom matches alongside
    // library matches (existing behavior — searching/picking a muscle pill
    // surfaces relevant customs too).
    if (!hasActiveScope || yourSavedOnly) return [];
    const q = query.trim().toLowerCase();
    return customExercises.filter((e) => {
      if (muscleFilter && (e.muscle || '').toLowerCase() !== muscleFilter.toLowerCase()) return false;
      if (q && !(e.name || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [customExercises, query, muscleFilter, hasActiveScope, yourSavedOnly]);

  // Library exercises store `equipment: [id, …]`. Custom exercises don't track equipment.
  const missingEquipmentFor = (ex) => {
    if (!ex || !Array.isArray(ex.equipment) || ex.equipment.length === 0) return [];
    if (profile.fullGym) return [];
    const owned = new Set(profile.equipment || []);
    return ex.equipment.filter((id) => !owned.has(id));
  };

  const completeSelect = (entry) => {
    onSelect({
      ...entry,
      isCustom: !entry.id || typeof entry.id === 'string',
      sets: entry.sets || (mode === 'replace' ? undefined : '3x8-12'),
    });
    setPendingPick(null);
    onClose();
  };

  const handleSelect = (entry) => {
    const missing = missingEquipmentFor(entry);
    if (missing.length > 0) {
      setPendingPick({ entry, missing });
      return;
    }
    completeSelect(entry);
  };

  const activateAndAdd = () => {
    if (!pendingPick) return;
    const { entry, missing } = pendingPick;
    const merged = Array.from(new Set([...(profile.equipment || []), ...missing]));
    update({ equipment: merged });
    completeSelect(entry);
  };

  const submitCustom = async () => {
    setError('');
    if (!customName.trim()) { setError('Give the exercise a name.'); return; }
    // If user has flagged "submit to library", show the heads-up confirmation
    // modal before the actual POST. Once submitted, only admins can remove
    // from the review queue. This guardrail prevents accidental clutter in
    // the admin queue (per user direction).
    if (submitToLibrary) {
      setSubmitConfirmOpen(true);
      return;
    }
    await actuallySubmitCustom();
  };

  const actuallySubmitCustom = async () => {
    setSubmitConfirmOpen(false);
    setBusy(true);
    try {
      // Normalize tags: split by comma, lowercase, trim, drop empties + dedupe.
      const tagList = Array.from(new Set(
        customTags.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean),
      ));
      const res = await axios.post(`${API}/custom-exercises`, {
        name: customName.trim(),
        muscle: customMuscle.trim() || null,
        notes: customNotes.trim() || null,
        tags: tagList,
        suggested_to_library: submitToLibrary,
      });
      const newEntry = res.data;
      setCustomExercises((arr) => [newEntry, ...arr]);
      completeSelect(newEntry);
      setCustomName(''); setCustomMuscle(''); setCustomNotes(''); setCustomTags(''); setSubmitToLibrary(true);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Could not save. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="bg-enf-surface-deep border-enf-surface-border text-enf-text-primary max-h-[90vh] overflow-y-auto">
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-2xl tracking-wide">
            {mode === 'replace' ? `Replace ${targetExerciseName || 'exercise'}` : 'Add exercise to today'}
          </SheetTitle>
          <SheetDescription className="text-enf-text-secondary text-xs">
            {eligibleLibrary.length}+ exercises in the Library and growing.
          </SheetDescription>
        </SheetHeader>

        {mode === 'replace' && targetExerciseName && (
          <div className="flex items-center gap-2 mt-4 mb-1 px-3 py-2 bg-enf-role-info/10 border border-enf-role-info/30 rounded-lg text-xs text-enf-role-info font-semibold">
            <Replace className="w-3.5 h-3.5 flex-shrink-0" />
            Replacing: {targetExerciseName}
          </div>
        )}

        <div className="flex gap-1 p-1 bg-enf-surface-mid rounded-md mt-4 mb-3 w-fit">
          <button onClick={() => setTab('library')}
            className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${tab === 'library' ? 'bg-enf-accent text-enf-text-primary' : 'text-enf-text-secondary'}`}
            data-testid="add-ex-tab-library">Library</button>
          <button onClick={() => setTab('custom')}
            className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${tab === 'custom' ? 'bg-enf-accent text-enf-text-primary' : 'text-enf-text-secondary'}`}
            data-testid="add-ex-tab-custom">Teach the coach</button>
        </div>

        {/* Equipment-missing banner takes over the body until resolved */}
        {pendingPick && (
          <div className="space-y-3 pb-3">
            <div className="border border-amber-500/40 bg-amber-500/5 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-enf-text-primary">
                  <div className="font-semibold mb-1">{pendingPick.entry.name}</div>
                  <div className="text-enf-text-secondary text-xs leading-relaxed">
                    This exercise requires{' '}
                    <span className="text-amber-300 font-semibold">
                      {pendingPick.missing.map((id) => EQUIPMENT_NAME[id] || id).join(', ')}
                    </span>{' '}
                    which isn't in your profile yet.
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={activateAndAdd}
              className="w-full py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-2"
              data-testid="add-ex-activate-equipment"
            >
              <Wrench className="w-4 h-4" />
              {pendingPick.missing.length === 1
                ? `Activate "${EQUIPMENT_NAME[pendingPick.missing[0]] || pendingPick.missing[0]}"`
                : `Activate ${pendingPick.missing.length} items`}
            </button>
            <button
              onClick={() => completeSelect(pendingPick.entry)}
              className="w-full py-2 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md text-xs font-medium text-enf-text-secondary transition-colors"
              data-testid="add-ex-add-once-anyway"
            >
              Add just for today anyway?
            </button>
            <button
              onClick={() => setPendingPick(null)}
              className="w-full py-2 text-xs text-enf-text-muted hover:text-enf-text-primary transition-colors"
            >
              Back to picker
            </button>
          </div>
        )}

        {tab === 'library' && !pendingPick && (
          <>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-enf-text-muted" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name…"
                className="w-full pl-9 pr-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-sm text-enf-text-primary placeholder:text-enf-text-muted focus:outline-none focus:border-enf-accent-fg"
                data-testid="add-ex-search"
              />
            </div>

            <div className="flex flex-wrap gap-1.5 mb-3">
              {MUSCLE_PILLS.map((m) => (
                <button
                  key={m}
                  onClick={() => { setMuscleFilter((cur) => (cur === m ? null : m)); setYourSavedOnly(false); }}
                  className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${
                    muscleFilter === m
                      ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary'
                      : 'bg-transparent border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary hover:border-enf-surface-border'
                  }`}
                  data-testid={`add-ex-pill-${m.toLowerCase().replace(/\s+/g, '-').replace(/\//g, '-')}`}
                >
                  {m}
                </button>
              ))}
              <button
                onClick={() => { setYourSavedOnly((v) => !v); setMuscleFilter(null); setActiveTag(null); }}
                className={`px-2.5 py-1 text-[11px] rounded-full border transition-colors ${
                  yourSavedOnly
                    ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary'
                    : 'bg-transparent border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary hover:border-enf-surface-border'
                }`}
                data-testid="add-ex-pill-your-saved"
              >
                Your Saved{(customExercises.length + savedRefs.length) > 0 ? ` (${customExercises.length + savedRefs.length})` : ''}
              </button>
              {(muscleFilter || yourSavedOnly) && (
                <button
                  onClick={() => { setMuscleFilter(null); setYourSavedOnly(false); setActiveTag(null); }}
                  className="px-2.5 py-1 text-[11px] rounded-full bg-enf-surface-mid border border-enf-surface-border text-enf-text-muted hover:text-enf-text-primary"
                  data-testid="add-ex-pill-clear"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Tag sub-filter — only when Your Saved is active and user has tags */}
            {yourSavedOnly && userTags.length > 0 && (
              <div className="flex flex-wrap items-center gap-1 mb-3" data-testid="add-ex-tag-subfilter">
                <span className="text-[9px] uppercase tracking-widest text-enf-text-muted font-semibold mr-1">Tags:</span>
                {userTags.map((t) => (
                  <button
                    key={t}
                    onClick={() => setActiveTag((cur) => (cur === t ? null : t))}
                    className={`px-2 py-0.5 text-[10px] rounded-full border transition-colors ${
                      activeTag === t
                        ? 'bg-enf-role-info border-enf-role-info text-black'
                        : 'bg-transparent border-enf-surface-border text-enf-text-secondary hover:text-enf-text-primary hover:border-enf-surface-border'
                    }`}
                    data-testid={`add-ex-tag-${t}`}
                  >
                    #{t}
                  </button>
                ))}
              </div>
            )}

            {!hasActiveScope ? (
              <div className="text-center py-10 px-4 text-xs text-enf-text-muted border border-dashed border-enf-surface-border rounded-lg" data-testid="add-ex-empty-state">
                <Search className="w-6 h-6 mx-auto mb-3 opacity-40" />
                <div className="text-enf-text-secondary mb-1">Pick a muscle group, type a name,</div>
                <div>or <button onClick={() => setTab('custom')} className="text-enf-role-info hover:text-enf-text-primary underline">ask your Coach</button> for something not yet in the Enferno library.</div>
              </div>
            ) : yourSavedOnly ? (
              // Your Saved view — unified list of customs + saved library refs.
              <div className="space-y-1 pb-3" data-testid="add-ex-saved-list">
                {filteredSaved.length === 0 ? (
                  <div className="text-center py-6 text-xs text-enf-text-muted">
                    {(customExercises.length + savedRefs.length) === 0
                      ? 'No saved exercises yet. Bookmark library exercises or teach the coach.'
                      : 'No saved exercises match this filter.'}
                  </div>
                ) : (
                  filteredSaved.map((row) => (
                    <ExerciseRow
                      key={`${row.kind}-${row.entry.id}`}
                      ex={row.entry}
                      mode={mode}
                      onSelect={handleSelect}
                      custom={row.kind === 'custom'}
                    />
                  ))
                )}
              </div>
            ) : (
              <>
                {filteredCustom.length > 0 && (
                  <div className="mb-3">
                    <div className="text-[10px] uppercase tracking-widest text-enf-role-info font-bold mb-1">Your saved</div>
                    <div className="space-y-1">
                      {filteredCustom.map((ex) => (
                        <ExerciseRow key={ex.id} ex={ex} mode={mode} onSelect={handleSelect} custom />
                      ))}
                    </div>
                  </div>
                )}

                <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-bold mb-1">Library ({filteredLibrary.length})</div>
                <div className="space-y-1 pb-3">
                  {filteredLibrary.map((ex) => (
                    <ExerciseRow key={ex.id} ex={ex} mode={mode} onSelect={handleSelect} />
                  ))}
                  {filteredLibrary.length === 0 && filteredCustom.length === 0 && (
                    <div className="text-center py-6 text-xs text-enf-text-muted">
                      No matches. Try <button onClick={() => setTab('custom')} className="text-enf-role-info hover:text-enf-text-primary underline">teach the coach</button>.
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {tab === 'custom' && !pendingPick && (
          <div className="space-y-3 pb-3">
            <div>
              <label className="block text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Exercise name</label>
              <input value={customName} onChange={(e) => setCustomName(e.target.value)}
                placeholder="e.g. Kettlebell halo"
                className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-sm text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                data-testid="add-ex-custom-name" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Muscle group (optional)</label>
              <input value={customMuscle} onChange={(e) => setCustomMuscle(e.target.value)}
                placeholder="e.g. Shoulders"
                className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-sm text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                data-testid="add-ex-custom-muscle" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Notes (optional)</label>
              <textarea value={customNotes} onChange={(e) => setCustomNotes(e.target.value)}
                rows={2}
                placeholder="Form cues, setup, equipment used…"
                className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-sm text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                data-testid="add-ex-custom-notes" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Tags (optional)</label>
              <input value={customTags} onChange={(e) => setCustomTags(e.target.value)}
                placeholder="e.g. combatives, hyrox, grip — comma separated"
                className="w-full px-3 py-2 bg-enf-surface-mid border border-enf-surface-border rounded-md text-sm text-enf-text-primary focus:outline-none focus:border-enf-accent-fg"
                data-testid="add-ex-custom-tags" />
              <div className="text-[10px] text-enf-text-muted mt-1">Tags let you build your own filters. Browse them via the "Your Saved" pill.</div>
            </div>
            <label className="flex items-start gap-2 cursor-pointer">
              <input type="checkbox" checked={submitToLibrary} onChange={(e) => setSubmitToLibrary(e.target.checked)}
                className="mt-1 accent-enf-accent" data-testid="add-ex-submit-library" />
              <div className="text-xs">
                <div className="text-enf-text-primary font-semibold flex items-center gap-1.5"><Sparkles className="w-3 h-3 text-enf-role-info" /> Submit to the Enferno Library</div>
                <div className="text-enf-text-muted mt-0.5">We'll review it for inclusion in the public exercise library. You'll still see it in your saved list either way.</div>
              </div>
            </label>

            {error && <div className="text-xs text-enf-accent-fg bg-enf-accent/10 border border-enf-accent-fg/30 rounded-md px-3 py-2">{error}</div>}

            <button onClick={submitCustom} disabled={busy || !customName.trim()}
              className="w-full py-2.5 bg-enf-accent hover:bg-enf-accent-hover disabled:opacity-50 rounded-md text-sm font-semibold transition-colors flex items-center justify-center gap-2"
              data-testid="add-ex-custom-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === 'replace' ? <Replace className="w-4 h-4" /> : <Plus className="w-4 h-4" />)}
              {busy ? 'Saving…' : (mode === 'replace' ? 'Replace with this' : 'Add to today')}
            </button>
          </div>
        )}

        {/* Heads-up confirmation modal — fires only when user opted to submit
            to the public library. Lets them back out before the suggestion
            hits the admin review queue, since only admins can remove from it. */}
        {submitConfirmOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="submit-library-confirm">
            <div className="bg-enf-surface-deep border border-enf-accent-fg/40 rounded-xl w-full max-w-md p-5">
              <div className="flex items-center gap-2 mb-3">
                <ShieldAlert className="w-4 h-4 text-enf-accent-fg" />
                <h3 className="font-display text-xl tracking-wide leading-none">Heads up — admin review</h3>
              </div>
              <p className="text-sm text-enf-text-secondary leading-relaxed mb-2">
                Submitting <span className="font-semibold text-enf-text-primary">"{customName}"</span> sends it to the Enferno admin queue for review.
              </p>
              <p className="text-xs text-enf-text-muted leading-relaxed mb-4">
                Once submitted, <span className="text-enf-text-secondary font-semibold">only an Enferno admin can remove it from the review queue</span>. You can still remove it from your own saved list any time.
              </p>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setSubmitConfirmOpen(false)}
                  className="px-3 py-1.5 text-xs font-semibold text-enf-text-secondary hover:text-enf-text-primary"
                  data-testid="submit-library-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={() => { setSubmitToLibrary(false); actuallySubmitCustom(); }}
                  className="px-3 py-1.5 text-xs font-semibold text-enf-text-primary hover:text-enf-text-primary bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded-md"
                  data-testid="submit-library-private-only"
                >
                  Save privately instead
                </button>
                <button
                  onClick={actuallySubmitCustom}
                  className="px-3 py-1.5 text-xs font-semibold text-enf-text-primary bg-enf-accent hover:bg-enf-accent-hover rounded-md"
                  data-testid="submit-library-confirm-go"
                >
                  Submit anyway
                </button>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

const ExerciseRow = ({ ex, mode, onSelect, custom = false }) => (
  <button
    type="button"
    onClick={() => onSelect(ex)}
    className="w-full text-left px-3 py-2.5 bg-enf-surface-mid hover:bg-enf-surface-mid border border-enf-surface-border rounded-md flex items-center justify-between gap-3 transition-colors group"
    data-testid={`add-ex-pick-${ex.id || ex.name}`}
  >
    <div className="min-w-0 flex-1">
      <div className="text-sm font-semibold text-enf-text-primary truncate flex items-center gap-2">
        {ex.name}
        {custom && <span className="text-[9px] uppercase tracking-widest text-enf-role-info bg-enf-role-info/10 border border-enf-role-info/30 rounded px-1 py-0.5">saved</span>}
        {ex.combatives && <span className="text-[9px] uppercase tracking-widest text-enf-role-warning bg-enf-role-warning/10 border border-enf-role-warning/40 rounded px-1 py-0.5" title="Combatives benefit">🔥</span>}
      </div>
      <div className="text-[11px] text-enf-text-muted truncate">
        {ex.muscle || (ex.equipment || []).join(', ') || 'Custom'}
      </div>
      {custom && (ex.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {ex.tags.map((t) => (
            <span key={t} className="text-[9px] text-enf-text-muted bg-enf-surface-mid rounded px-1.5 py-0.5">#{t}</span>
          ))}
        </div>
      )}
    </div>
    <div className="flex items-center gap-1 text-enf-accent-fg opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
      {mode === 'replace' ? <Replace className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
      <Check className="w-3 h-3" />
    </div>
  </button>
);

export default AddExerciseSheet;
